import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import domainValidator from 'utils/validators/domainValidator'
import tinValidator from 'utils/validators/tinValidator'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import LoginField from 'modules/Forms/Account/Create/subcomponents/LoginField'
import FormFrame from 'modules/Forms/Account/Create/subcomponents/FormFrame'
import formStyles from 'modules/Forms/common-form.module.scss'

export const OzonPerformanceSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    legal_entity_name: Yup.string().required('common::validation_messages.required'),
    network: Yup.string().required('common::validation_messages.required'),
    seller_url: Yup.string().test(
      'domainValidator',
      'common::validation_messages.incorrect_url',
      domainValidator,
    ),
    website_url: Yup.string().test(
      'domainValidator',
      'common::validation_messages.incorrect_url',
      domainValidator,
    ),
    legal_entity_tax_number: Yup.string()
      .test(
        'tinValidator',
        'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.validation_messages.incorrect_tin_number',
        tinValidator,
      )
      .required('common::validation_messages.required'),
  })

let CreateOzonPerformanceAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  form,
  networkId,
  autofill,
  array,
  multiLogins,
  networkType,
}) => {
  const { t } = useTranslation()

  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  return (
    <FormFrame
      onSubmit={handleSubmit(submitAccountData)}
      multiLogins={multiLogins}
      form={form}
      submitAccountData={submitAccountData}
      submitting={submitting}
      onClose={onClose}
    >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />
        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.appnext.fields.login.label'}
          />
        </div>
        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="legal_entity_name"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.ozon-performance.fields.advertiser_name.label',
            )}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="legal_entity_tax_number"
            component={renderInputField}
            label={t('services::forms.register_account.ozon-performance.fields.tax_number.label')}
            format={(value) => (value === null ? '' : '' + value)}
            parse={(value) => value.replace(/\D/g, '').slice(0, 12) || null}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="seller_url"
            type="url"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ozon-performance.fields.seller_url.label')}
            t={t}
            normalize={(value) => value.trim()}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="website_url"
            type="url"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ozon-performance.fields.website_url.label')}
            t={t}
            normalize={(value) => value.trim()}
          />
        </div>

    </FormFrame>
  )
}

CreateOzonPerformanceAccountForm = reduxForm({
  form: 'CreateOzonPerformanceAccountForm',
  initialValues: {
    login: '',
    network: '',
    legal_entity_name: '',
    legal_entity_tax_number: '',
    seller_url: '',
    website_url: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateOzonPerformanceAccountForm)

CreateOzonPerformanceAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  networkType: PropTypes.string,
}

export default CreateOzonPerformanceAccountForm
